import React, { useContext } from "react";
import { Typography, Button, Slider, message, Divider } from "antd";
import "../css/ChangeLocationContent.css";
import GlobalMap from "../util/GlobalMap";
import { LocationContext } from "../LocationContext";

const { Title, Text } = Typography;

const ChangeLocationContent = ({
    onApply,
    onReset,
    height = "180px",
    isOpen = false
}) => {
    const {
        latitude,
        longitude,
        radius,
        selectedAddress
    } = useContext(LocationContext);

    // ✅ Local state to prevent premature filtering and lag
    const [tmpRadius, setTmpRadius] = React.useState(radius);
    const [tmpPos, setTmpPos] = React.useState(
        latitude && longitude ? { lat: parseFloat(latitude), lng: parseFloat(longitude) } : null
    );
    const [tmpAddress, setTmpAddress] = React.useState(selectedAddress);
    const [tmpCity, setTmpCity] = React.useState(localStorage.getItem("cityName") || "");

    // Keep local states in sync only when props/context are explicitly reset or mounted
    React.useEffect(() => {
        setTmpRadius(radius);
        setTmpAddress(selectedAddress);
        setTmpCity(localStorage.getItem("cityName") || "");
        if (latitude && longitude) {
            setTmpPos({ lat: parseFloat(latitude), lng: parseFloat(longitude) });
        } else {
            setTmpPos(null);
        }
    }, [radius, latitude, longitude, selectedAddress]);

    // ✅ Apply selected location
    const handleLocalApply = () => {
        if (!tmpPos || !tmpPos.lat || !tmpPos.lng) {
            return message.warning("Please select a location on the map first.");
        }
        onApply({
            lat: tmpPos.lat,
            lng: tmpPos.lng,
            radius: tmpRadius,
            address: tmpAddress,
            city: tmpCity
        });
    };

    const handleLocationSelect = React.useCallback((data) => {
        setTmpPos({ lat: data.latitude, lng: data.longitude });
        setTmpAddress(data.address);
        setTmpCity(data.city);
    }, []);

    return (
        <div className="change-location-container">
            <div className="change-location-card">
                {/* Header (Top centered) */}
                <div className="location-header-section">
                    <Title level={5} className="pcu-margin-0 pcu-color-purple" style={{ fontSize: "16px", textAlign: "center" }}>
                        Choose your Location
                    </Title>
                    <Text type="secondary" style={{ fontSize: "11px", display: "block", textAlign: "center", marginBottom: "12px" }}>
                        Exact detection is recommended
                    </Text>
                </div>

                {/* Map (Full Width) */}
                <div className="location-map-section">
                    <GlobalMap
                        onLocationSelect={handleLocationSelect}
                        height={height}
                        markerPosition={tmpPos}
                        radius={tmpRadius}
                        address={tmpAddress}
                        onReset={onReset}
                        isOpen={isOpen}
                    />
                </div>

                {/* Address Display (Centered below the map) */}
                <div className="location-address-center">
                    <Text style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "var(--nearzon-purple, #0B1F4D)",
                        display: "block",
                        textAlign: "center",
                        margin: "0 auto",
                        width: "fit-content"
                    }}>
                        {tmpAddress || "No location selected"}
                    </Text>
                </div>

                {/* Bottom Row: Radius (Left) & Apply Button (Right) */}
                <div className="location-bottom-actions-row">
                    {/* Radius (Left) */}
                    <div className="location-bottom-left">
                        <div className="radius-container-wrapper">
                            <span className="location-field-label">Radius: {tmpRadius} km</span>
                            <div className="radius-section">
                                <Slider
                                    min={1}
                                    max={50}
                                    value={tmpRadius}
                                    onChange={(val) => setTmpRadius(val)}
                                    style={{ flex: 1, margin: "0 8px" }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Apply Button (Right) */}
                    <div className="location-bottom-right">
                        <div className="apply-container-wrapper">
                            <span className="location-field-label" style={{ opacity: 0, pointerEvents: "none" }}>Apply</span>
                            <div className="location-action-footer">
                                <Button type="primary" onClick={handleLocalApply} className="apply-btn">
                                    Apply Location
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangeLocationContent;
